import { ShieldWarningIcon } from '@phosphor-icons/react';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import { changePasswordRequest } from '@/services/auth';

/**
 * Plan 06-03 ForcePasswordChangePage at `/force-change-password`.
 *
 * Reachable when the AuthContext user has `passwordChangeRequired === true`. On submit,
 * calls `POST /api/auth/change-password` (allow-listed by the backend's force-change
 * filter, see Plan 06-02). On success, drops the local session (without re-calling the
 * backend logout — the just-rotated tokens would race with /api/auth/logout) and routes
 * to `/login` so the user re-authenticates with the new password — that fresh login
 * issues a token WITHOUT `force_password_change=true`, which is the cleanest path
 * forward.
 */

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'New password must be at least 8 characters')
      .regex(/[a-z]/, 'Must contain a lowercase letter')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/\d/, 'Must contain a digit')
      .regex(/[^a-zA-Z0-9]/, 'Must contain a non-alphanumeric character'),
    confirmPassword: z.string().min(1, 'Please confirm the new password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'New password and confirmation do not match',
  });

type PasswordFormValues = z.input<typeof passwordSchema>;

export function ForcePasswordChangePage(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useZodForm({
    schema: passwordSchema,
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  // If the user is NOT actually force-flagged, send them home — this page is a one-way gate.
  if (user !== null && !user.passwordChangeRequired) {
    return <Navigate to="/dashboard" replace />;
  }

  async function onSubmit(values: PasswordFormValues): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await changePasswordRequest({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });
      // Drop the session WITHOUT calling /api/auth/logout (the access token still has
      // force_password_change=true and the just-rotated password would race with logout).
      await logout({ skipBackendCall: true });
      toast.success('Password changed', {
        description: 'Please sign in again with your new password.',
      });
      void navigate('/login', { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMessage(e.detail ?? 'Could not change password.');
        toast.error('Could not change password', { description: e.detail ?? e.message });
      } else {
        setErrorMessage('Network error — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-gradient-to-br from-warning/10 via-background to-destructive/10 min-h-screen flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center space-y-3">
          <div className="h-16 w-16 rounded-2xl bg-warning text-warning-foreground flex items-center justify-center mx-auto">
            <ShieldWarningIcon size={32} weight="bold" />
          </div>
          <CardTitle className="text-xl">Change your password</CardTitle>
          <CardDescription>
            Your account requires a password change before you can continue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage !== null && (
            <div
              role="alert"
              data-testid="force-change-error"
              className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
            >
              {errorMessage}
            </div>
          )}
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="current-password">Current password</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.currentPassword !== undefined}
                {...form.register('currentPassword')}
                data-testid="force-change-current"
              />
              {form.formState.errors.currentPassword && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.currentPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.newPassword !== undefined}
                {...form.register('newPassword')}
                data-testid="force-change-new"
              />
              {form.formState.errors.newPassword && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                disabled={submitting}
                aria-invalid={form.formState.errors.confirmPassword !== undefined}
                {...form.register('confirmPassword')}
                data-testid="force-change-confirm"
              />
              {form.formState.errors.confirmPassword && (
                <p className="text-xs text-destructive" data-testid="force-change-confirm-error">
                  {form.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="force-change-submit"
            >
              {submitting ? 'Changing password…' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
